import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const location = useLocation();
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-[1400px] mx-auto">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors duration-200"
          >
            <div className="w-9 h-9 flex items-center justify-center">
              <i className="ri-book-read-line text-2xl text-primary-500"></i>
            </div>
            <span className="text-xl font-heading font-bold whitespace-nowrap">亲亲漫画</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-primary-500 bg-primary-100'
                  : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
              }`}
            >
              首页
            </Link>

            <Link
              to="/category/hanman"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname.startsWith('/category/')
                  ? 'text-primary-500 bg-primary-100'
                  : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
              }`}
            >
              分类
            </Link>

            <div className="relative" ref={categoryRef}>
              <button
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap flex items-center gap-1 cursor-pointer"
              >
                漫画大全
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {isCategoryOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-background-50 rounded-lg border border-background-200/70 py-2 shadow-lg z-50">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </div>
                      <span>{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-500">{cat.count}部</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/about'
                  ? 'text-primary-500 bg-primary-100'
                  : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              <div className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
                <i className="ri-search-line text-sm text-foreground-500"></i>
              </div>
              <input
                type="text"
                placeholder="搜索漫画..."
                className="w-48 pl-9 pr-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-all duration-200"
              />
            </div>
            <a
              href="#download"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              APP下载
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className="block px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors duration-150"
            >
              首页
            </Link>
            <Link
              to="/category/hanman"
              className="block px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors duration-150"
            >
              分类浏览
            </Link>
            <Link
              to="/about"
              className="block px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors duration-150"
            >
              关于我们
            </Link>
            <div className="pt-2">
              <a
                href="#download"
                className="block w-full text-center px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200"
              >
                APP下载
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}